import { ArrowDropDown, PlayArrowOutlined, ScheduleOutlined } from '@mui/icons-material'
import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker'
import type { DateTime } from 'luxon'
import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'

type RunTestButtonProps = {
  label: string
  onRun: (startAt: string | null) => void
  disabled?: boolean
  size?: 'small' | 'medium'
  variant?: 'contained' | 'outlined' | 'text'
  startIcon?: ReactNode
  testId?: string
}

/**
 * Split button used wherever a test or a whole market can be queued: the primary
 * press hands it to the controller queue (which works through queued tests one at
 * a time), the caret opens "Queue now / Schedule for later…", and scheduling pops
 * a date-time picker that defers the start.
 */
function RunTestButton({
  label,
  onRun,
  disabled = false,
  size = 'small',
  variant = 'contained',
  startIcon = <PlayArrowOutlined />,
  testId,
}: Readonly<RunTestButtonProps>) {
  const { t } = useTranslation()
  const menuId = useId()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [isScheduleOpen, setIsScheduleOpen] = useState(false)
  const [startAt, setStartAt] = useState<DateTime | null>(null)

  function runNow() {
    setMenuAnchor(null)
    onRun(null)
  }

  function openSchedule() {
    setMenuAnchor(null)
    setIsScheduleOpen(true)
  }

  function closeSchedule() {
    setIsScheduleOpen(false)
    setStartAt(null)
  }

  function confirmSchedule() {
    const iso = startAt?.toISO()
    if (!iso) return
    onRun(iso)
    closeSchedule()
  }

  return (
    <>
      <ButtonGroup
        disabled={disabled}
        size={size}
        sx={{
          transition: 'transform 140ms cubic-bezier(0.22, 1, 0.36, 1)',
          '&:active': { transform: 'scale(0.98)' },
          '& .MuiButton-startIcon': { transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)' },
          '&:hover .MuiButton-startIcon': { transform: 'translateX(2px)' },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:active': { transform: 'none' },
            '& .MuiButton-startIcon': { transition: 'none' },
            '&:hover .MuiButton-startIcon': { transform: 'none' },
          },
        }}
        variant={variant}
      >
        <Button data-testid={testId} startIcon={startIcon} onClick={runNow}>
          {label}
        </Button>
        <Button
          aria-controls={menuAnchor ? menuId : undefined}
          aria-expanded={menuAnchor ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label={t('prequalifications.resource_set.test.more_run_options')}
          size={size}
          sx={{ px: 0.5 }}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>

      <Menu anchorEl={menuAnchor} id={menuId} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={runNow}>
          <ListItemIcon>
            <PlayArrowOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('prequalifications.resource_set.test.run_now')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={openSchedule}>
          <ListItemIcon>
            <ScheduleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('prequalifications.resource_set.test.schedule')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog fullWidth maxWidth="xs" open={isScheduleOpen} onClose={closeSchedule}>
        <DialogTitle>{t('prequalifications.resource_set.schedule.title')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DesktopDateTimePicker
              disablePast
              label={t('prequalifications.resource_set.schedule.label')}
              slotProps={{ textField: { fullWidth: true, sx: { mt: 1 } } }}
              value={startAt}
              onChange={setStartAt}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <CustomButton variant="outlined" onClick={closeSchedule}>
            {t('common.button.cancel')}
          </CustomButton>
          <CustomButton disabled={!startAt} variant="contained" onClick={confirmSchedule}>
            {t('prequalifications.resource_set.schedule.confirm')}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default RunTestButton
